import {
  CalendarAgenda20Filled,
  CalendarAgenda20Regular,
  Note20Filled,
  Note20Regular,
  CalendarMonth20Filled,
  CalendarMonth20Regular,
  ClipboardTextLtr20Filled,
  ClipboardTextLtr20Regular,
  Document20Filled,
  Document20Regular,
  Folder20Filled,
  Folder20Regular,
  History20Filled,
  MailInbox20Filled,
  MailInbox20Regular,
  History20Regular,
  GanttChart20Filled,
  GanttChart20Regular,
  GlanceHorizontal20Filled,
  GlanceHorizontal20Regular,
  Money20Filled,
  Money20Regular,
  PeopleCommunity20Filled,
  PeopleCommunity20Regular,
  PeopleTeam20Filled,
  PeopleTeam20Regular,
  TaskListSquareLtr20Filled,
  TaskListSquareLtr20Regular,
  type FluentIcon,
} from "@fluentui/react-icons";
import { normalizeProjectTab, type ProjectTab } from "@/store/persist";

export type ProjectNavItem = { value: ProjectTab; label: string; icon: FluentIcon; iconOn: FluentIcon };
export type ProjectNavGroup = { id: string; label?: string; items: ProjectNavItem[] };

/**
 * 플랜 사이드바 메뉴. 새 화면은 알맞은 그룹에 한 줄로 추가한다.
 * 맨 위 「계획」은 일정·현황·주간 보고. 매일 여는 작업·이슈는 「실행」.
 */
export const PROJECT_NAV: ProjectNavGroup[] = [
  {
    id: "plan",
    label: "계획",
    items: [
      { value: "schedule", label: "일정", icon: GanttChart20Regular, iconOn: GanttChart20Filled },
      { value: "reports", label: "현황", icon: GlanceHorizontal20Regular, iconOn: GlanceHorizontal20Filled },
      { value: "weekly", label: "주간 보고", icon: CalendarAgenda20Regular, iconOn: CalendarAgenda20Filled },
      { value: "review", label: "회고", icon: Note20Regular, iconOn: Note20Filled },
    ],
  },
  {
    id: "work",
    label: "실행",
    items: [
      { value: "tasks", label: "작업", icon: TaskListSquareLtr20Regular, iconOn: TaskListSquareLtr20Filled },
      { value: "intake", label: "요구사항", icon: MailInbox20Regular, iconOn: MailInbox20Filled },
      { value: "records", label: "이슈", icon: ClipboardTextLtr20Regular, iconOn: ClipboardTextLtr20Filled },
      { value: "docs", label: "문서", icon: Document20Regular, iconOn: Document20Filled },
      { value: "files", label: "파일", icon: Folder20Regular, iconOn: Folder20Filled },
      { value: "calendar", label: "캘린더", icon: CalendarMonth20Regular, iconOn: CalendarMonth20Filled },
      { value: "activity", label: "활동", icon: History20Regular, iconOn: History20Filled },
    ],
  },
  {
    id: "resources",
    label: "자원",
    items: [
      { value: "team", label: "배정", icon: PeopleTeam20Regular, iconOn: PeopleTeam20Filled },
      { value: "stakeholders", label: "이해관계자", icon: PeopleCommunity20Regular, iconOn: PeopleCommunity20Filled },
      { value: "budget", label: "예산", icon: Money20Regular, iconOn: Money20Filled },
    ],
  },
];

const PLAN_ROOTS = new Set(["plans", "projects"]);

/** `/plans/:id/...` 또는 옛 `/projects/:id/...` → id. `/projects/new`는 플랜이 아니다. */
export function projectIdFromPath(pathname: string): string | null {
  const [root, id] = pathname.split("/").filter(Boolean);
  return root && PLAN_ROOTS.has(root) && id && id !== "new" ? id : null;
}

/** 주소창에 보이는 플랜 경로. 옛 `/projects`는 이 주소로 보낸다. */
export function planHref(planId: string, tab: string = "schedule"): string {
  return `/plans/${planId}/${tab}`;
}

export function pathnameOpensPlan(pathname: string, planId?: string): boolean {
  const id = projectIdFromPath(pathname);
  if (!id) return false;
  return planId ? id === planId : true;
}

/** `/projects/:id/<tab>[/...]` → tab, defaulting to schedule. 옛 경로(board·analytics·overview)는 흡수된 탭으로 본다. */
export function tabFromPath(pathname: string): ProjectTab {
  const seg = pathname.split("/").filter(Boolean)[2];
  return normalizeProjectTab(seg) ?? "schedule";
}
